import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";

export const qboSlice = createApi({
  reducerPath: "qboSlice",
  tagTypes: ["qboData"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getSyncData: build.query<
      {
        id: string;
        syncCustomer: boolean;
        syncInvoice: boolean;
        syncPayments: boolean;
        syncServiceItems: boolean;
        connectWithQBO: boolean;
        isSynced: boolean;
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `api/org/sync-data/${orgId}`,
        method: "GET",
      }),
      providesTags: ["qboData"],
    }),
    getRefreshedToken: build.query<
      { accessToken: string },
      { orgId: string; userId: string }
    >({
      query: ({ orgId, userId }) => ({
        url: `/qbo/refresh-token`,
        method: "GET",
        params: { orgId, userId },
      }),
      providesTags: ["qboData"],
    }),
    getQboCustomers: build.query<
      {
        name: string;
        email: string;
        id: string;
        qboCustomerId: string;
        qboName: string;
        qboEmail: string;
      }[],
      { orgId: string; userId: string }
    >({
      query: ({ orgId, userId }) => ({
        url: `api/quickbook/get-customers`,
        method: "GET",
        params: { orgId, userId },
      }),
      providesTags: ["qboData"],
    }),
    disconnectWithQbo: build.query<unknown, { orgId: string; userId: string }>({
      query: ({ orgId, userId }) => ({
        url: `qbo/disconnect`,
        method: "GET",
        params: { orgId, userId },
      }),
      providesTags: ["qboData"],
    }),

    updateSyncData: build.mutation<
      unknown,
      {
        syncCustomer: boolean;
        syncInvoice: boolean;
        syncPayments: boolean;
        syncServiceItems: boolean;
        id: string;
        connectWithQbo: boolean;
        isSynced: boolean;
      }
    >({
      query: ({
        syncCustomer,
        syncInvoice,
        syncPayments,
        syncServiceItems,
        id,
      }) => ({
        url: "api/org/update-data-sync",
        method: "PUT",
        body: {
          syncCustomer,
          syncInvoice,
          syncPayments,
          syncServiceItems,
          id,
          connectWithQbo: true,
          isSynced: true,
        },
      }),
      invalidatesTags: ["qboData"],
    }),
    reviewResult: build.mutation<
      unknown,
      {
        operation: string;
        entityId: string;
        qboEntityId: string;
        entityName: string;
        orgId: string;
      }[]
    >({
      query: (body) => {
        return {
          url: `api/quickbook/review-result/${body[0].orgId}`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: ["qboData"],
    }),
    startSync: build.mutation<
      unknown,
      {
        orgId: string;
        userId: string;
      }
    >({
      query: (params) => {
        return {
          url: `/qbo/start-sync`,
          method: "POST",
          params,
        };
      },
      invalidatesTags: ["qboData"],
    }),
  }),
});
export const {
  useLazyGetSyncDataQuery,
  useReviewResultMutation,
  useUpdateSyncDataMutation,
  useLazyGetQboCustomersQuery,
  useLazyGetRefreshedTokenQuery,
  useStartSyncMutation,
  useLazyDisconnectWithQboQuery,
} = qboSlice;
